const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001'

class TerrariumApiService {
  constructor() {
    this.broadcasterLogin = null
  }

  setBroadcasterLogin(login) {
    this.broadcasterLogin = login
  }

  getBaseUrl() {
    if (!this.broadcasterLogin) {
      throw new Error('Broadcaster login not set')
    }
    return `${API_BASE}/twitch/terrarium/api/broadcaster/${this.broadcasterLogin}`
  }

  // ============================================
  // COMBINED
  // ============================================

  async loadAll() {
    try {
      const response = await fetch(`${this.getBaseUrl()}/entities`, {
        credentials: 'include'
      })
      if (!response.ok) {
        if (response.status === 404) return { plants: [], insects: [] }
        throw new Error(`Failed to load entities: ${response.status}`)
      }
      return await response.json()
    } catch (error) {
      console.error('Error loading entities:', error)
      return { plants: [], insects: [] }
    }
  }

  // ============================================
  // PLANTS (uses existing /plants endpoints)
  // ============================================

  async loadPlants() {
    try {
      const response = await fetch(`${this.getBaseUrl()}/plants`, {
        credentials: 'include'
      })
      if (!response.ok) return []
      return await response.json()
    } catch (error) {
      console.error('Error loading plants:', error)
      return []
    }
  }

  async addPlant(userId, username, plantType) {
    try {
      const response = await fetch(`${this.getBaseUrl()}/plants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId, username, plantType })
      })
      if (!response.ok) {
        const error = await response.json()
        console.error('Failed to add plant:', error)
        return null
      }
      return await response.json()
    } catch (error) {
      console.error('Error adding plant:', error)
      return null
    }
  }

  async waterPlant(plantUserId, actorUserId, actorUsername) {
    try {
      const response = await fetch(`${this.getBaseUrl()}/plants/${plantUserId}/water`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ actorUserId, actorUsername })
      })
      if (!response.ok) return null
      return await response.json()
    } catch (error) {
      console.error('Error watering plant:', error)
      return null
    }
  }

  async growPlants(growthRate, healthDecayRate, waterDurationMinutes) {
    try {
      const response = await fetch(`${this.getBaseUrl()}/plants/grow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ growthRate, healthDecayRate, waterDurationMinutes })
      })
      if (!response.ok) return null
      return await response.json()
    } catch (error) {
      console.error('Error growing plants:', error)
      return null
    }
  }

  async removePlant(userId) {
    try {
      const response = await fetch(`${this.getBaseUrl()}/plants/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      })
      return response.ok
    } catch (error) {
      console.error('Error removing plant:', error)
      return false
    }
  }

  async removePlantByUsername(username) {
    try {
      const response = await fetch(`${this.getBaseUrl()}/plants/by-username/${username}`, {
        method: 'DELETE',
        credentials: 'include'
      })
      return response.ok
    } catch (error) {
      console.error('Error removing plant:', error)
      return false
    }
  }

  async removeAllPlants() {
    try {
      const response = await fetch(`${this.getBaseUrl()}/plants`, {
        method: 'DELETE',
        credentials: 'include'
      })
      return response.ok
    } catch (error) {
      console.error('Error removing plants:', error)
      return false
    }
  }

  // ============================================
  // INSECTS
  // ============================================

  async loadInsects() {
    try {
      const response = await fetch(`${this.getBaseUrl()}/insects`, {
        credentials: 'include'
      })
      if (!response.ok) return []
      return await response.json()
    } catch (error) {
      console.error('Error loading insects:', error)
      return []
    }
  }

  async addInsect(userId, username, insectType) {
    try {
      const response = await fetch(`${this.getBaseUrl()}/insects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId, username, insectType })
      })
      if (!response.ok) {
        const error = await response.json()
        console.error('Failed to add insect:', error)
        return null
      }
      return await response.json()
    } catch (error) {
      console.error('Error adding insect:', error)
      return null
    }
  }

  async removeInsect(userId) {
    try {
      const response = await fetch(`${this.getBaseUrl()}/insects/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      })
      return response.ok
    } catch (error) {
      console.error('Error removing insect:', error)
      return false
    }
  }

  async removeInsectByUsername(username) {
    try {
      const response = await fetch(`${this.getBaseUrl()}/insects/by-username/${username}`, {
        method: 'DELETE',
        credentials: 'include'
      })
      return response.ok
    } catch (error) {
      console.error('Error removing insect:', error)
      return false
    }
  }

  async removeAllInsects() {
    try {
      const response = await fetch(`${this.getBaseUrl()}/insects`, {
        method: 'DELETE',
        credentials: 'include'
      })
      return response.ok
    } catch (error) {
      console.error('Error removing insects:', error)
      return false
    }
  }

  async setEntityVariant(userId, entityType, variant) {
    try {
      const response = await fetch(`${this.getBaseUrl()}/entities/${userId}/variant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityType, variant })
      })
      return response.ok
    } catch (error) {
      console.error('Error setting variant:', error)
      return false
    }
  }

  async setEntityType(userId, entityType, newType) {
    try {
      const response = await fetch(`${this.getBaseUrl()}/entities/${userId}/type`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityType, newType })
      })
      return response.ok
    } catch (error) {
      console.error('Error setting type:', error)
      return false
    }
  }
}

export const terrariumApi = new TerrariumApiService()
export default terrariumApi